/**
 * backTarget.js — v149.6
 *
 * Carries "where the visitor came from" across a link that opens a NEW
 * browsing context, using the `?back=` query param this codebase already
 * uses for the in-app viewer (see `livePreviewHref` in
 * components/quickPromote/AdPerformance.jsx).
 *
 * WHY A PARAM AND NOT THE BROWSER'S OWN SIGNALS: a Quick Promote product
 * ad on someone's bio page opens `/p/{product_id}` with
 * `target="_blank" rel="noreferrer"`. Measured in that new tab:
 *
 *     history.length    === 1     -> navigate(-1) is a no-op
 *     document.referrer === ""    -> stripped by rel="noreferrer"
 *     window.opener     === null  -> severed by the same attribute
 *
 * So the origin is not recoverable from anything the browser provides.
 * It has to travel in the URL or it is simply gone.
 */

// Reject C0 controls and DEL. Written as a code-point scan rather than a
// regex with \u escapes on purpose: escape sequences have a habit of being
// resolved into literal control BYTES by whatever writes the file, which
// leaves invisible characters sitting in source. This form cannot be
// mangled that way.
function hasControlChars(v) {
  for (let i = 0; i < v.length; i += 1) {
    const code = v.charCodeAt(i);
    if (code < 32 || code === 127) return true;
  }
  return false;
}

/**
 * Validate a `?back=` value before navigating to it.
 *
 * This is an open-redirect guard, not a formality: without it,
 * `/p/{id}?back=https://evil.example` would turn our own Back button into
 * an off-site redirect on a public, unauthenticated page that gets shared
 * around. Only same-origin, root-relative paths are allowed through.
 *
 * Returns the path, or null when the value can't be trusted.
 */
export function safeBackPath(raw) {
  if (!raw) return null;
  const v = String(raw).trim();
  // Must be root-relative…
  if (!v.startsWith("/")) return null;
  // …but NOT protocol-relative (`//evil.example` resolves off-origin),
  // and not the backslash variants browsers normalise into it.
  if (v.startsWith("//")) return null;
  if (v.includes("\\")) return null;
  // Control characters can smuggle a target past the checks above.
  if (hasControlChars(v)) return null;
  return v;
}

/**
 * Append `?back=<currentPath>` to a link that will open in a new tab, so
 * the destination can offer a real way back.
 *
 * Only the pathname is carried — the current query string is deliberately
 * dropped so transient params (`?qp_preview=…`, UTM tags) don't ride along
 * and re-trigger preview modes on the return trip.
 *
 * Leaves `href` untouched when it already carries a `back`, when it isn't
 * a root-relative path, or when there's no sensible origin to record.
 */
export function withBackParam(href, fromPath) {
  if (!href || typeof href !== "string") return href;
  if (!href.startsWith("/") || href.startsWith("//")) return href;
  const from = safeBackPath(
    fromPath ?? (typeof window !== "undefined" ? window.location.pathname : null),
  );
  if (!from) return href;
  const [path, hash] = href.split("#");
  if (/[?&]back=/.test(path)) return href;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}back=${encodeURIComponent(from)}${hash ? `#${hash}` : ""}`;
}
